<script>
import AoikumoDisabledTooltip from 'dashboard/components/dahreply/AoikumoDisabledTooltip.vue';
import AoikumoOutletTag from 'dashboard/components/dahreply/AoikumoOutletTag.vue';

export default {
  name: 'BranchCard',
  components: {
    AoikumoDisabledTooltip,
    AoikumoOutletTag,
  },
  props: {
    branch: {
      type: Object,
      required: true,
      validator: (branch) => {
        return branch.id && branch.name;
      }
    },
    loading: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isHovered: false
    };
  },
  computed: {
    isReadOnly() {
      return (
        this.readOnly ||
        this.$store.getters['aoikumo/isLinkedBranch'](this.branch)
      );
    },
    branchId() {
      return this.branch.id;
    },
    branchName() {
      return this.branch.name;
    },
    phoneNumber() {
      return this.branch.phone_number;
    },
    branchAddress() {
      return this.branch.address;
    },
    isActive() {
      return this.branch.is_active;
    },
    cardClasses() {
      return {
        'opacity-50 cursor-not-allowed pointer-events-none': !this.isActive,
        'cursor-pointer hover:shadow-lg hover:border-slate-300 dark:hover:border-slate-600': this.isActive,
        'animate-pulse': this.loading
      };
    },
    ariaLabel() {
      return `${this.branchName} branch${!this.isActive ? ' (inactive)' : ''}`;
    }
  },
  methods: {
    onEdit(event) {
      if (this.loading || this.isReadOnly) return;
      event.stopPropagation();
      this.$emit('edit', this.branch);
    },
    onDelete(event) {
      if (this.loading || this.isReadOnly) return;
      event.stopPropagation();
      this.$emit('delete', {
        name: this.branchName,
        id: this.branchId
      });
    },
    onNavigate() {
      if (this.loading) return;
      if (this.isActive) {
        this.$emit('navigate', this.branchId);
      }
    },
    onMouseEnter() {
      if (!this.loading && this.isActive) {
        this.isHovered = true;
      }
    },
    onMouseLeave() {
      this.isHovered = false;
    }
  },
  emits: ['edit', 'delete', 'navigate']
};
</script>

<template>
  <div
    class="relative bg-white dark:bg-slate-800 dark:text-white p-5 rounded-lg shadow-md border border-slate-200 dark:border-slate-700 transition-all duration-200"
    :class="cardClasses"
    @click="onNavigate"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    :aria-label="ariaLabel"
    :aria-disabled="!isActive"
    role="button"
    tabindex="0"
    @keydown.enter="onNavigate"
    @keydown.space="onNavigate"
  >
    <!-- Loading overlay -->
    <div
      v-if="loading"
      class="absolute inset-0 bg-white dark:bg-slate-800 bg-opacity-75 rounded-lg flex items-center justify-center z-10"
    >
      <fluent-icon icon="spinner" class="animate-spin h-6 w-6 text-primary" />
    </div>

    <!-- Hover Actions -->
    <transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <AoikumoDisabledTooltip
        v-if="isHovered && !loading && isActive"
        :disabled="isReadOnly"
        class="absolute top-3 right-3 flex gap-1 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg p-1 z-20"
        role="toolbar"
        aria-label="Branch actions"
      >
        <!-- Edit Button -->
        <button
          :disabled="isReadOnly"
          @click.stop="onEdit"
          class="p-2 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
          :aria-label="$t('BRANCHES.INDEX.ACTIONS.EDIT')"
          :title="$t('BRANCHES.INDEX.ACTIONS.EDIT')"
        >
          <fluent-icon icon="edit" class="h-4 w-4" />
        </button>

        <!-- Delete Button -->
        <button
          :disabled="isReadOnly"
          @click.stop="onDelete"
          class="p-2 rounded-md text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
          :aria-label="$t('BRANCHES.INDEX.ACTIONS.DELETE')"
          :title="$t('BRANCHES.INDEX.ACTIONS.DELETE')"
        >
          <fluent-icon icon="delete" class="h-4 w-4" />
        </button>
      </AoikumoDisabledTooltip>
    </transition>

    <!-- Static Actions (shown when not hovered but card is active) -->
    <div
      v-if="!isHovered && !loading && isActive"
      class="absolute top-3 right-3 opacity-60"
    >
      <fluent-icon icon="more-horizontal" class="h-5 w-5 text-slate-400" />
    </div>

    <!-- Content -->
    <div class="flex items-center gap-4">
      <div 
        class="flex-shrink-0 h-10 w-10 rounded-lg bg-primary-50 dark:bg-primary-900 flex items-center justify-center"
        :class="{ 'opacity-50': !isActive }"
      >
        <fluent-icon 
          icon="building-bank" 
          class="h-6 w-6 text-primary" 
        />
      </div>
      
      <div class="min-w-0 flex-1">
        <h4 
          class="text-lg font-semibold text-slate-800 dark:text-white truncate"
          :title="branchName"
        >
          {{ branchName }}
        </h4>
        <AoikumoOutletTag :branch="branch" class="mt-1" />
        
        <div class="text-sm text-slate-600 dark:text-slate-300 mt-1 space-y-1">
          <p 
            v-if="phoneNumber" 
            class="flex items-center gap-2 truncate"
          >
            <fluent-icon icon="call" class="h-4 w-4 flex-shrink-0" />
            <span class="truncate">{{ phoneNumber }}</span>
          </p>
          <p 
            v-if="branchAddress" 
            class="flex items-start gap-2"
          >
            <fluent-icon icon="location" class="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span class="break-words line-clamp-2">{{ branchAddress }}</span>
          </p>
        </div>

        <!-- Status badge -->
        <div v-if="!isActive" class="mt-2">
          <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300">
            <fluent-icon icon="warning" class="h-3 w-3 mr-1" />
            Inactive
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Focus styles for accessibility */
[role="button"]:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* Ensure the action buttons are properly sized */
button {
  min-width: 2rem;
  min-height: 2rem;
}
</style>